import { Currencies } from './currencies.enum';
import { Features } from './feature-per-product.constant';
import { NumericIdentifiable } from './identifiable.model';
import { Price } from './price.model';
import { PublicationCalendarBenefit } from './publication-product.model';

export interface Product extends NumericIdentifiable {
  code: ProductCode;
  name: string;
  slug: string;
  mainTaxon: { code: string } | null;
  variants: Variant[];

  // Enriched fields
  features: ProductFeature[];
  calendar: PublicationCalendarBenefit;
  longname: string;
  currency: Currencies;
  description: string;
  shortName?: string;
  basePrice: number;
  cmsDiscountPrice?: Price;
}

export interface Variant extends NumericIdentifiable {
  code: string;
  name: string | null;
  currencyCode: string;
  prices: Array<{ price: number; quantity: number }>;
}

// WARNING - order matters in this enum - see sortProducts function
export enum ProductCode {
  JS24FREE = 'JS24FREE', // price 0
  JS24SOLO = 'JS24SOLO',
  JS24PLUS = 'JS24PLUS',
  JS24BASIC = 'JS24BASIC',
  JS24ADVANCED = 'JS24ADVANCED',
  JOBSSTARTER = 'JOBSSTARTER',
  JOBSSTANDARD = 'JOBSSTANDARD', // join integration, not using on UI
  JOBSFLEX = 'JOBSFLEX', // used only for upsell landing page
  JOBSOFFER22FREE = 'JOBSOFFER22FREE',
  JOBSLIMITED = 'JOBSLIMITED',
  JOBSOFFER22XS = 'JOBSOFFER22XS', // aka "Jobs Lite"
  JOBSOFFER22S = 'JOBSOFFER22S', // aka "Jobs Basic"
  JOBSOFFER22M = 'JOBSOFFER22M', // aka "Jobs Advanced"
  JOBSOFFER22L = 'JOBSOFFER22L', // aka "Jobs Ultimate"
  JOBSOFFER22XL = 'JOBSOFFER22XL', // aka "Jobs Ultimate" Plus
  JOBUP24FREE = 'JOBUP24FREE',
  JOBUP24LITE = 'JOBUP24LITE',
  JOBUP24BASIC = 'JOBUP24BASIC',
  JOBUP24ADVANCED = 'JOBUP24ADVANCED',
  JOBUP24ULTIMATE = 'JOBUP24ULTIMATE',
  JOBUP24ULTIMATEPLUS = 'JOBUP24ULTIMATEPLUS',
  JOBUPAPPRENTICESHIP = 'JOBUPAPPRENTICESHIP',
  JOBSAPPRENTICESHIP = 'JOBSAPPRENTICESHIP',
  PAYPERPERFORMANCE = 'PAYPERPERFORMANCE',
  JOBSSPONTANEOUSAPPLICATION = 'JOBSSPONTANEOUSAPPLICATION',
  JOBUPSPONTANEOUSAPPLICATION = 'JOBUPSPONTANEOUSAPPLICATION',
  JOBSOFFSITE = 'JOBSOFFSITE',
  JOBUPOFFSITE = 'JOBUPOFFSITE',
  JOBSGIFTEDFREE = 'JOBSGIFTEDFREE',
  JOBUPGIFTEDFREE = 'JOBUPGIFTEDFREE',
  VBPPAID = 'VBPPAID', // aka "Performance"
  VBPFREE = 'VBPFREE', // the free allowance of "Performance"
}

export interface ProductFeature {
  id: Features;
  parent?: string;
  title?: string;
  description?: string;
  translation?: string;
}

export type ActiveTab = 'products' | 'credits';

type ProductPerformanceMapping = Record<ProductCode, string>;

const PERFORMANCE_TRANSLATION_KEY: ProductPerformanceMapping = {
  [ProductCode.JOBSOFFER22S]: 'PERFORMANCE_REACH_JOBSOFFER22S',
  [ProductCode.JOBUP24BASIC]: 'PERFORMANCE_REACH_JOBUP24BASIC',
  [ProductCode.JOBUP24LITE]: 'PERFORMANCE_REACH_JOBUP24LITE',
  [ProductCode.JOBSOFFER22XS]: 'PERFORMANCE_REACH_JOBSOFFER22XS',
  [ProductCode.JOBSOFFER22M]: 'PERFORMANCE_REACH_JOBSOFFER22M',
  [ProductCode.JOBUP24ADVANCED]: 'PERFORMANCE_REACH_JOBUP24ADVANCED',
  [ProductCode.JOBSOFFER22L]: 'PERFORMANCE_REACH_JOBSOFFER22L',
  [ProductCode.JOBUP24ULTIMATE]: 'PERFORMANCE_REACH_JOBUP24ULTIMATE',
  [ProductCode.JOBSOFFER22XL]: 'PERFORMANCE_REACH_JOBSOFFER22XL',
  [ProductCode.JOBUP24ULTIMATEPLUS]: 'PERFORMANCE_REACH_JOBUP24ULTIMATEPLUS',
  [ProductCode.JS24FREE]: 'PERFORMANCE_REACH_JS24FREE',
  [ProductCode.JS24SOLO]: 'PERFORMANCE_REACH_JS24SOLO',
  [ProductCode.JS24PLUS]: 'PERFORMANCE_REACH_JS24PLUS',
  [ProductCode.JS24BASIC]: 'PERFORMANCE_REACH_JS24BASIC',
  [ProductCode.JS24ADVANCED]: 'PERFORMANCE_REACH_JS24ADVANCED',
  [ProductCode.JOBSSTARTER]: 'PERFORMANCE_REACH_JOBSSTARTER',
  [ProductCode.JOBSSTANDARD]: 'PERFORMANCE_REACH_JOBSSTANDARD',
  [ProductCode.JOBSFLEX]: 'PERFORMANCE_REACH_JOBSFLEX',
  [ProductCode.JOBSOFFER22FREE]: 'PERFORMANCE_REACH_JOBSOFFER22FREE',
  [ProductCode.JOBSLIMITED]: 'PERFORMANCE_REACH_JOBSLIMITED',
  [ProductCode.JOBUP24FREE]: 'PERFORMANCE_REACH_JOBUP24FREE',
  [ProductCode.JOBUPAPPRENTICESHIP]: 'PERFORMANCE_REACH_JOBUPAPPRENTICESHIP',
  [ProductCode.JOBSAPPRENTICESHIP]: 'PERFORMANCE_REACH_JOBSAPPRENTICESHIP',
  [ProductCode.PAYPERPERFORMANCE]: 'PERFORMANCE_REACH_PAYPERPERFORMANCE',
  [ProductCode.JOBSSPONTANEOUSAPPLICATION]: 'PERFORMANCE_REACH_JOBSSPONTANEOUSAPPLICATION',
  [ProductCode.JOBUPSPONTANEOUSAPPLICATION]: 'PERFORMANCE_REACH_JOBUPSPONTANEOUSAPPLICATION',
  [ProductCode.JOBSOFFSITE]: 'PERFORMANCE_REACH_JOBSOFFSITE',
  [ProductCode.JOBUPOFFSITE]: 'PERFORMANCE_REACH_JOBUPOFFSITE',
  [ProductCode.JOBSGIFTEDFREE]: 'PERFORMANCE_REACH_JOBSGIFTEDFREE',
  [ProductCode.JOBUPGIFTEDFREE]: 'PERFORMANCE_REACH_JOBUPGIFTEDFREE',
  [ProductCode.VBPPAID]: 'PERFORMANCE_REACH_VBPPAID',
  [ProductCode.VBPFREE]: 'PERFORMANCE_REACH_VBPFREE',
};

export const getPerformanceKey = (productCode: ProductCode) =>
  PERFORMANCE_TRANSLATION_KEY[productCode];
